import React, { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, publicFileUrl } from "@/lib/api";
import GironeBadge from "@/components/GironeBadge";
import { GitCompareArrows, Trophy, Shuffle, ArrowLeftRight } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useSearchParams } from "react-router-dom";

const SHIELD_PLACEHOLDER =
  "https://images.unsplash.com/photo-1577214322326-5b31d9d886b0?crop=entropy&cs=srgb&fm=jpg&w=300&q=80";

export default function Confronto() {
  const [teams, setTeams] = useState([]);
  const [standings, setStandings] = useState([]);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [aData, setAData] = useState(null);
  const [bData, setBData] = useState(null);
  const [aAch, setAAch] = useState(null);
  const [bAch, setBAch] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/public/teams").then(({ data }) => setTeams(data || [])).catch(() => {});
    api.get("/public/standings").then(({ data }) => setStandings(data || [])).catch(() => {});
    const qA = params.get("a"); const qB = params.get("b");
    if (qA) setAId(qA);
    if (qB) setBId(qB);
  }, []);

  useEffect(() => {
    if (!aId) { setAData(null); setAAch(null); return; }
    api.get(`/public/teams/${aId}`).then(({ data }) => setAData(data)).catch(() => setAData(null));
    api.get(`/public/achievements/${aId}`).then(({ data }) => setAAch(data)).catch(() => setAAch(null));
    const next = new URLSearchParams(params); next.set("a", aId); setParams(next, { replace: true });
  }, [aId]);
  useEffect(() => {
    if (!bId) { setBData(null); setBAch(null); return; }
    api.get(`/public/teams/${bId}`).then(({ data }) => setBData(data)).catch(() => setBData(null));
    api.get(`/public/achievements/${bId}`).then(({ data }) => setBAch(data)).catch(() => setBAch(null));
    const next = new URLSearchParams(params); next.set("b", bId); setParams(next, { replace: true });
  }, [bId]);

  const standingById = useMemo(() => {
    const m = {};
    (standings || []).forEach((r) => { m[r.user_id] = r; });
    return m;
  }, [standings]);

  const swap = () => { const t = aId; setAId(bId); setBId(t); };
  const shuffle = () => {
    if (teams.length < 2) return;
    const a = teams[Math.floor(Math.random() * teams.length)];
    let b = teams[Math.floor(Math.random() * teams.length)];
    while (b.user_id === a.user_id) b = teams[Math.floor(Math.random() * teams.length)];
    setAId(a.user_id); setBId(b.user_id);
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-6">
        <div className="fade-up flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(59,86,224,0.15)", color: "#3b56e0" }}>
            <GitCompareArrows className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">FIFE 2026/27 · Head-to-Head</div>
            <h1 className="display font-black text-4xl md:text-5xl">Confronto Squadre</h1>
          </div>
        </div>
        <p className="text-slate-500 max-w-2xl">
          Scegli due squadre per confrontare bacheca trofei, statistiche di stagione e piazzamento
          nel campionato. Utile per costruire rivalità e sfide dirette.
        </p>

        <div className="card-surface rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-end gap-3">
          <TeamPicker label="Squadra A" value={aId} onChange={setAId} teams={teams} testid="picker-a" />
          <div className="flex items-center justify-center gap-2 mt-1 md:mt-0">
            <Button variant="outline" size="sm" className="rounded-full border-slate-300" onClick={swap}
                    data-testid="confronto-swap">
              <ArrowLeftRight className="w-4 h-4 mr-1" /> Inverti
            </Button>
            <Button variant="outline" size="sm" className="rounded-full border-slate-300" onClick={shuffle}
                    data-testid="confronto-shuffle">
              <Shuffle className="w-4 h-4 mr-1" /> Sorteggia
            </Button>
          </div>
          <TeamPicker label="Squadra B" value={bId} onChange={setBId} teams={teams} testid="picker-b" />
        </div>

        {(aData && bData) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="confronto-content">
            <TeamColumn team={aData} standing={standingById[aId]} ach={aAch} side="A" />
            <TeamColumn team={bData} standing={standingById[bId]} ach={bAch} side="B" />
          </div>
        ) : (
          <div className="card-surface rounded-2xl p-8 text-center text-slate-500" data-testid="confronto-empty">
            Seleziona due squadre per iniziare il confronto.
          </div>
        )}

        {aData && bData && (
          <ComparisonBar aStanding={standingById[aId]} bStanding={standingById[bId]}
                          aAch={aAch} bAch={bAch}
                          aName={aData.team_name} bName={bData.team_name} />
        )}
      </div>
      <Footer />
    </div>
  );
}

function TeamPicker({ label, value, onChange, teams, testid }) {
  const by = { A: [], B: [], C: [], D: [] };
  (teams || []).forEach((t) => by[t.girone]?.push(t));
  return (
    <div className="flex-1">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full h-11 rounded-xl" data-testid={testid}>
          <SelectValue placeholder="Scegli una squadra…" />
        </SelectTrigger>
        <SelectContent>
          {["A", "B", "C", "D"].map((g) => (
            <React.Fragment key={g}>
              <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-400">Girone {g}</div>
              {by[g].map((t) => (
                <SelectItem key={t.user_id} value={t.user_id} data-testid={`${testid}-opt-${t.user_id}`}>
                  {t.team_name}
                </SelectItem>
              ))}
            </React.Fragment>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function TeamColumn({ team, standing, ach, side }) {
  const gironeAccent = { A: "#3b56e0", B: "#f97316", C: "#06b6d4", D: "#a855f7" };
  const accent = gironeAccent[team.girone] || "#64748b";
  return (
    <div className="card-surface rounded-2xl overflow-hidden fade-up" data-testid={`confronto-col-${side}`}>
      <div className="px-5 py-4 flex items-center gap-3"
           style={{ background: `${accent}12`, borderBottom: `1px solid ${accent}33` }}>
        <img src={team.shield_path ? publicFileUrl(team.shield_path) : SHIELD_PLACEHOLDER}
             onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }}
             alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="display font-black text-xl truncate">{team.team_name}</div>
          <div className="text-slate-500 text-sm truncate">{team.coach_name || "—"}</div>
          <div className="mt-1 flex items-center gap-1.5">
            <GironeBadge girone={team.girone} size={16} />
            <span className="text-slate-500 text-[10px] uppercase tracking-widest font-bold">Girone {team.girone}</span>
          </div>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <StatsGrid standing={standing} />
        <TrofeiRow ach={ach} />
      </div>
    </div>
  );
}

function StatsGrid({ standing }) {
  const s = standing || {};
  const cells = [
    { label: "Punti", value: s.points ?? "–", strong: true },
    { label: "Total Points", value: (s.total_points != null ? Number(s.total_points).toFixed(1) : "–") },
    { label: "V - N - P", value: `${s.wins ?? 0} - ${s.draws ?? 0} - ${s.losses ?? 0}` },
    { label: "GF - GS", value: `${s.gf ?? 0} - ${s.gs ?? 0}` },
    { label: "Diff. Reti", value: s.dr != null ? (s.dr > 0 ? `+${s.dr}` : s.dr) : "–" },
    { label: "Giornate", value: s.played ?? 0 },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {cells.map((c) => (
        <div key={c.label} className="rounded-xl bg-slate-50 p-2.5 text-center border border-slate-100">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">{c.label}</div>
          <div className={`display ${c.strong ? "font-black text-2xl" : "font-bold text-lg"} text-slate-900 tabular-nums leading-tight`}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function TrofeiRow({ ach }) {
  const unlocked = ach?.unlocked || [];
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Trophy className="w-4 h-4 text-amber-500" />
        <div className="display font-black text-sm text-amber-900">Trofei ({unlocked.length}{ach?.total ? ` / ${ach.total}` : ""})</div>
      </div>
      {unlocked.length === 0 ? (
        <div className="text-slate-500 text-xs italic">Nessun trofeo sbloccato.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {unlocked.map((a) => (
            <div key={a.id}
                 title={`${a.name} · ${a.description}`}
                 className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-amber-200 text-xs"
                 data-testid={`trofeo-${a.id}`}>
              <span className="text-base leading-none">{a.icon}</span>
              <span className="font-bold text-amber-900 truncate max-w-[110px]">{a.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ComparisonBar({ aStanding, bStanding, aAch, bAch, aName, bName }) {
  const rows = [
    { label: "Punti", a: aStanding?.points ?? 0, b: bStanding?.points ?? 0 },
    { label: "Vittorie", a: aStanding?.wins ?? 0, b: bStanding?.wins ?? 0 },
    { label: "Gol Fatti", a: aStanding?.gf ?? 0, b: bStanding?.gf ?? 0 },
    { label: "Trofei", a: (aAch?.unlocked?.length) ?? 0, b: (bAch?.unlocked?.length) ?? 0 },
    { label: "Total Points", a: Math.round(aStanding?.total_points || 0), b: Math.round(bStanding?.total_points || 0) },
  ];
  return (
    <div className="card-surface rounded-2xl p-5" data-testid="comparison-bar">
      <div className="display font-black text-lg mb-4">Chi vince nei numeri?</div>
      <div className="space-y-3">
        {rows.map((r) => {
          const total = Math.max(1, (r.a || 0) + (r.b || 0));
          const aPct = ((r.a || 0) / total) * 100;
          const bPct = ((r.b || 0) / total) * 100;
          const aWin = (r.a || 0) > (r.b || 0);
          const bWin = (r.b || 0) > (r.a || 0);
          return (
            <div key={r.label}>
              <div className="flex items-center justify-between text-xs font-bold mb-1">
                <span className={aWin ? "text-emerald-700" : "text-slate-500"}>{r.a}</span>
                <span className="uppercase tracking-widest text-slate-500">{r.label}</span>
                <span className={bWin ? "text-emerald-700" : "text-slate-500"}>{r.b}</span>
              </div>
              <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-slate-100">
                <div className={`h-full ${aWin ? "bg-emerald-500" : "bg-slate-300"}`} style={{ width: `${aPct}%` }} />
                <div className={`h-full ${bWin ? "bg-emerald-500" : "bg-slate-300"}`} style={{ width: `${bPct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-4 text-center">
        {aName} <span className="mx-2">vs</span> {bName}
      </div>
    </div>
  );
}
